import { Component, EventEmitter, Input, OnInit, Output, type SimpleChanges } from '@angular/core';
import { NgbActiveModal, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { FlightEnterpriseSearchInterface, ServiceProvider } from '@sabstravtech/obtservices/base';
import {
  EnterpriseSearchService,
  UserService,
  StorageService,
  InputFlightJourney,
  ServiceType,
  WithSubscriptionComponent,
  FlightOptions,
  FlightExtrasOptions
} from '@sabstravtech/obtservices/angular';
import { FormControl, FormGroup } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { LightningUserFavorurite } from '../../../../../core/classes/user-favourite.enum';

type ExtrasKey =
  'flightExtras' | 'baggageAllowance' | 'advancedAncillaryOptions' | 'reducedMobillityAssistance';

@Component({
  selector: 'app-flight-extras',
  templateUrl: './flight-extras.component.html',
  styleUrls: ['./flight-extras.component.scss'],
  providers: [CurrencyPipe],
  standalone: false
})
export class FlightExtrasComponent extends WithSubscriptionComponent implements OnInit {
  permittedService: string[] = [
    ServiceProvider.Travelfusion,
    ServiceProvider.TravelportFlight,
    ServiceProvider.AmadeusFlight
  ];
  // Flights on the same journey (Outbound or Inbound)
  @Input() flights: InputFlightJourney[] = [];
  @Input() extraOptions: FlightOptions;
  // Current flight ID
  @Input() flightId: string = '';
  @Input() travellerId: string = '';
  @Output() flightExtras: EventEmitter<any> = new EventEmitter<any>();
  @Input() selectedFlightExtrasChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() supplier: string = '';
  @Input() showTFXLuggage = false;
  @Input() hideAncillary = false;
  @Input() hideExtras = false;
  @Input() parentTabId: string = '';
  @Input() extrasPlaceholder: FlightExtrasOptions = null;
  @Input() baggagePlaceholder: FlightExtrasOptions = null;
  @Input() ancillaryPlaceholder: FlightExtrasOptions = null;
  ServiceProvider = ServiceProvider;
  public form: FormGroup;
  searchParams: FlightEnterpriseSearchInterface;
  // This flag is used to hide Luggage Options, Flight Extras and Advanced Ancillary Options
  // Reduced Mobillity Assistance should not be hidden
  showExtras = false;
  emptyOption: any = { text: 'None' };
  focus$: Record<ExtrasKey, Subject<string>> = {
    flightExtras: new Subject<string>(),
    baggageAllowance: new Subject<string>(),
    advancedAncillaryOptions: new Subject<string>(),
    reducedMobillityAssistance: new Subject<string>()
  };
  click$: Record<ExtrasKey, Subject<string>> = {
    flightExtras: new Subject<string>(),
    baggageAllowance: new Subject<string>(),
    advancedAncillaryOptions: new Subject<string>(),
    reducedMobillityAssistance: new Subject<string>()
  };
  selectedFlightExtras: {
    flightExtras: FlightExtrasOptions;
    baggageAllowance: FlightExtrasOptions;
    advancedAncillaryOptions: FlightExtrasOptions;
    reducedMobillityAssistance: FlightExtrasOptions;
  } = {
    flightExtras: this.emptyOption,
    baggageAllowance: this.emptyOption,
    advancedAncillaryOptions: this.emptyOption,
    reducedMobillityAssistance: this.emptyOption
  };
  displayWarning = false;
  isSabreRyanair = false;
  constructor(
    public activeModal: NgbActiveModal,
    public searchService: EnterpriseSearchService,
    public userService: UserService,
    public storageService: StorageService,
    private currencyPipe: CurrencyPipe,
    private translate: TranslateService
  ) {
    super();
    this.emptyOption.text = this.translate.instant('None');
  }

  formatOption = (opt: FlightExtrasOptions | null): string => {
    if (!opt || !opt.text) return '';
    const price = opt.price ? this.currencyPipe.transform(opt.price, opt.currency) : '';
    return price ? `${opt.text} ${price}` : opt.text;
  };

  get extrasOptionList(): FlightExtrasOptions[] {
    const head =
      this.displayWarning && this.extrasPlaceholder ? this.extrasPlaceholder : this.emptyOption;
    return [head, ...(this.extraOptions?.extras ?? [])];
  }

  get luggageOptionList(): FlightExtrasOptions[] {
    const head =
      this.displayWarning && this.baggagePlaceholder ? this.baggagePlaceholder : this.emptyOption;
    return [head, ...(this.extraOptions?.luggage ?? [])];
  }

  get ancillaryOptionList(): FlightExtrasOptions[] {
    const head =
      this.displayWarning && this.ancillaryPlaceholder
        ? this.ancillaryPlaceholder
        : this.emptyOption;
    return [head, ...(this.extraOptions?.ancillary ?? [])];
  }

  get mobilityOptionList(): FlightExtrasOptions[] {
    return [this.emptyOption, ...(this.extraOptions?.mobility ?? [])];
  }

  searchExtras = (text$: Observable<string>) =>
    this.buildSearch(text$, 'flightExtras', () => this.extrasOptionList);
  searchLuggage = (text$: Observable<string>) =>
    this.buildSearch(text$, 'baggageAllowance', () => this.luggageOptionList);
  searchAncillary = (text$: Observable<string>) =>
    this.buildSearch(text$, 'advancedAncillaryOptions', () => this.ancillaryOptionList);
  searchMobility = (text$: Observable<string>) =>
    this.buildSearch(text$, 'reducedMobillityAssistance', () => this.mobilityOptionList);

  private buildSearch(
    text$: Observable<string>,
    key: ExtrasKey,
    getOptions: () => FlightExtrasOptions[]
  ): Observable<FlightExtrasOptions[]> {
    return merge(text$, this.focus$[key], this.click$[key]).pipe(map(() => getOptions()));
  }

  ngOnInit() {
    this.searchParams = this.searchService.searches[ServiceType.Flight];

    const seatMapConfigs = this.userService.getUserFavoriteObject<{
      GDS: { showSeatMaps: boolean };
      SabreLowCost: { showSeatMaps: boolean; showExtras: boolean };
      LCC: { showSeatMaps: boolean; showExtras: boolean };
      NDC: { showSeatMaps: boolean };
    }>(LightningUserFavorurite.FlightResultsConfiguration);

    this.showExtras =
      this.supplier === (ServiceProvider.Travelfusion as string)
        ? seatMapConfigs?.LCC?.showExtras
        : true;

    if (this.permittedService.includes(this.supplier)) {
      // Subscribe for changes to update the extras on the same journey
      this.subscribe(this.selectedFlightExtrasChange, ({ data, key, updatingFlightId }) => {
        if (
          !this.flights.some((flight) => flight.id === updatingFlightId) ||
          updatingFlightId === this.flightId
        ) {
          return;
        }
        this.selectedFlightExtras[key] = data[key];
        this.form?.get(key)?.setValue(data[key] ?? this.emptyOption, { emitEvent: false });
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.form) {
      this.form = new FormGroup({
        flightExtras: new FormControl(this.emptyOption),
        baggageAllowance: new FormControl(this.emptyOption),
        advancedAncillaryOptions: new FormControl(this.emptyOption),
        reducedMobillityAssistance: new FormControl(this.emptyOption)
      });
    }
    if (changes.supplier && changes.supplier.previousValue !== changes.supplier.currentValue) {
      this.isSabreRyanair =
        this.supplier === ServiceProvider.SabreFlight &&
        this.flights.some(
          (flight) =>
            flight.marketingCarrierName === 'Ryanair' || flight.operatingCarrierName === 'Ryanair'
        );
    }

    if (changes.parentTabId?.currentValue === this.parentTabId) {
      // Initialize selectedFlightExtras from placeholders if available
      if (this.extrasPlaceholder && Object.keys(this.extrasPlaceholder).length > 0) {
        this.selectedFlightExtras.flightExtras = this.extrasPlaceholder;
        this.form?.get('flightExtras')?.setValue(this.extrasPlaceholder, { emitEvent: false });
      }
      if (this.baggagePlaceholder && Object.keys(this.baggagePlaceholder).length > 0) {
        this.selectedFlightExtras.baggageAllowance = this.baggagePlaceholder;
        this.form?.get('baggageAllowance')?.setValue(this.baggagePlaceholder, { emitEvent: false });
      }
      if (this.ancillaryPlaceholder && Object.keys(this.ancillaryPlaceholder).length > 0) {
        this.selectedFlightExtras.advancedAncillaryOptions = this.ancillaryPlaceholder;
        this.form
          ?.get('advancedAncillaryOptions')
          ?.setValue(this.ancillaryPlaceholder, { emitEvent: false });
      }

      if (!this.showTFXLuggage && !this.isSabreRyanair) {
        this.form?.get('baggageAllowance')?.disable();
      }

      if (this.hideAncillary) {
        this.form?.get('advancedAncillaryOptions')?.disable();
      }

      if (this.hideExtras) {
        this.form?.get('flightExtras')?.disable();
      }
      if (!this.showTFXLuggage && this.hideAncillary && this.hideExtras) {
        this.displayWarning = true;
      } else {
        this.displayWarning = false;
      }
      if (this.isSabreRyanair && !this.form?.get('baggageAllowance').value?.id) {
        const fristFreeLuggage = this.extraOptions?.luggage?.find((luggage) => !luggage.price);
        if (fristFreeLuggage) {
          this.form.get('baggageAllowance').setValue(fristFreeLuggage);
          this.selectedFlightExtras.baggageAllowance = fristFreeLuggage;
          this.updateFlightExtras('baggageAllowance');
        }
      }
    }
  }

  updateFlightExtras(key: string, event?: NgbTypeaheadSelectItemEvent) {
    // Update selectedFlightExtras: use form value if enabled, keep existing if disabled
    // ngbTypeahead emits selectItem BEFORE writing the form value, so prefer $event.item when present
    if (this.form.get(key)?.enabled) {
      this.selectedFlightExtras[key] = event ? event.item : this.form.value[key];
    }

    this.flightExtras.emit({
      data: this.selectedFlightExtras,
      flightIds: this.flights.map((flight) => flight.id), // List of flightIds on the same journey
      updatingFlightId: this.flightId,
      travellerId: this.travellerId,
      key // Updating object key
    });
  }
}
